import { useState } from 'react';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { api } from '../lib/api.js';
import { useSession } from '../lib/store.js';
import { Button, Card, Field, TextInput, NumberInput, TextArea, Choice } from '../components/ui.jsx';
import { toStone, fmtDate } from '../lib/format.js';

// Seven short steps. Only weight is required — the brief lists waist, hips and
// exact weight as unknowns and says explicitly that they must not hold up the
// programme, so every one of those can be skipped and filled in later.

const STEPS = ['goal', 'you', 'measurements', 'activity', 'health', 'training', 'targets'];

export default function Onboarding() {
  const { bootstrap } = useSession();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [suggested, setSuggested] = useState(null);

  const [form, setForm] = useState({
    name: '',
    start_date: new Date().toISOString().slice(0, 10),
    target_date: '2027-05-28',
    goal_note: '',
    age: 32,
    height_cm: 163,
    weight_kg: '',
    clothing_start: 'UK 12/14',
    clothing_goal: 'UK 10',
    measurements: { waist_cm: '', hips_cm: '', bust_cm: '', thigh_cm: '', arm_cm: '' },
    baseline_steps: 5000,
    health: {
      osteoporosis: true,
      hormonal_context: 'Early menopause / POI / perimenopause — exact term not settled clinically.',
      hrt_note: 'Estradiol gel and cyclical progesterone.',
      supplements_note: 'Calcichew D3 Forte, two tablets nightly.',
      migraine_prone: true,
      migraine_triggers: 'Under-fuelling and dehydration, particularly around exercise.',
      clinician_notes: '',
    },
    training_days: [1, 2, 4],
    session_minutes: 40,
    targets: { kcal_low: '', kcal_high: '', protein_g: '', step_target: '' },
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setHealth = (patch) => setForm((f) => ({ ...f, health: { ...f.health, ...patch } }));
  const setMeasure = (patch) => setForm((f) => ({ ...f, measurements: { ...f.measurements, ...patch } }));
  const setTargets = (patch) => setForm((f) => ({ ...f, targets: { ...f.targets, ...patch } }));

  async function next() {
    setError(null);

    if (STEPS[step] === 'you' && !form.weight_kg) {
      setError('A rough starting weight is the one thing the programme genuinely needs. An estimate is fine.');
      return;
    }

    // Work out the suggested targets as the user arrives at that step.
    if (STEPS[step] === 'training') {
      try {
        const t = await api.post('/targets', {
          weight_kg: form.weight_kg,
          height_cm: form.height_cm,
          age: form.age,
          baseline_steps: form.baseline_steps,
        });
        setSuggested(t);
        setTargets({ kcal_low: t.kcal_low, kcal_high: t.kcal_high, protein_g: t.protein_g, step_target: t.step_target });
      } catch (err) {
        setError(err.message);
        return;
      }
    }

    if (step === STEPS.length - 1) return finish();
    setStep((s) => s + 1);
  }

  async function finish() {
    setBusy(true);
    setError(null);
    try {
      if (form.name.trim()) await api.patch('/me', { name: form.name.trim() });
      await api.post('/onboarding', form);
      await bootstrap();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const current = STEPS[step];

  return (
    <div className="min-h-screen flex flex-col max-w-lg mx-auto w-full px-gutter pb-10">
      <div className="pt-8 pb-5">
        <div className="flex gap-1.5 mb-5" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-accent' : 'bg-panelHi'}`} />
          ))}
        </div>
        <p className="text-label uppercase text-textMute">
          Step {step + 1} of {STEPS.length}
        </p>
      </div>

      <Card className="flex-1">
        {current === 'goal' && (
          <>
            <h1 className="text-page-title mb-3">Welcome.</h1>
            <p className="text-body text-textSoft mb-4">
              This programme runs to <strong className="text-text">{fmtDate(form.target_date)}</strong> — around eight months. The aim is
              to look and feel noticeably better in your clothes, get properly strong, and look after your bones, without any of the
              severe dieting that has made you feel unwell before.
            </p>
            <p className="text-body text-textSoft mb-5">
              Three sessions a week, 30 to 45 minutes each. Normal food. No progress photos, no calorie-counting every gram, and no app
              telling you off for a weekend away.
            </p>
            <Field label="What should the app call you?" id="name">
              <TextInput id="name" value={form.name} onChange={(e) => set({ name: e.target.value })} autoComplete="given-name" autoFocus />
            </Field>
            <Field label="Anything you want to write down about why you are doing this?" id="goal" hint="Optional. It will be here on the days it helps to read it.">
              <TextArea id="goal" value={form.goal_note} onChange={(e) => set({ goal_note: e.target.value })} placeholder="e.g. I want to feel like myself in my own clothes again." />
            </Field>
          </>
        )}

        {current === 'you' && (
          <>
            <h1 className="text-page-title mb-4">A few numbers</h1>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Age" id="age">
                <NumberInput id="age" value={form.age} onChange={(e) => set({ age: e.target.value })} />
              </Field>
              <Field label="Height" id="height">
                <NumberInput id="height" suffix="cm" value={form.height_cm} onChange={(e) => set({ height_cm: e.target.value })} />
              </Field>
            </div>
            <Field
              label="Starting weight"
              id="weight"
              hint={form.weight_kg ? `That is about ${toStone(Number(form.weight_kg))}.` : 'In kilograms. A rough figure is fine — it is a starting point, not a verdict.'}
            >
              <NumberInput id="weight" suffix="kg" step="0.1" value={form.weight_kg} onChange={(e) => set({ weight_kg: e.target.value })} autoFocus />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Clothes now" id="cs">
                <TextInput id="cs" value={form.clothing_start} onChange={(e) => set({ clothing_start: e.target.value })} />
              </Field>
              <Field label="Heading towards" id="cg">
                <TextInput id="cg" value={form.clothing_goal} onChange={(e) => set({ clothing_goal: e.target.value })} />
              </Field>
            </div>
          </>
        )}

        {current === 'measurements' && (
          <>
            <h1 className="text-page-title mb-2">Starting measurements</h1>
            <p className="text-body-sm text-textSoft mb-5">
              Skip this entirely if you do not have a tape measure to hand — you can add them any time. These matter more than the scales
              do, because they are what actually tracks changing shape.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['waist_cm', 'Waist', 'Narrowest point, usually just above the belly button.'],
                ['hips_cm', 'Hips', 'Widest point.'],
                ['bust_cm', 'Bust', null],
                ['thigh_cm', 'Thigh', 'Mid-thigh, same leg each time.'],
                ['arm_cm', 'Upper arm', null],
              ].map(([key, label, hint]) => (
                <Field key={key} label={label} id={key} hint={hint}>
                  <NumberInput
                    id={key}
                    suffix="cm"
                    step="0.5"
                    value={form.measurements[key]}
                    onChange={(e) => setMeasure({ [key]: e.target.value })}
                  />
                </Field>
              ))}
            </div>
          </>
        )}

        {current === 'activity' && (
          <>
            <h1 className="text-page-title mb-2">How much do you move now?</h1>
            <p className="text-body-sm text-textSoft mb-5">
              Roughly, on a normal working day. Now the job is desk-based, this number does more for body composition than any extra gym
              session would — so it is worth being honest about where it starts.
            </p>
            <Field label="Typical daily steps" id="steps">
              <Choice
                columns={2}
                value={form.baseline_steps}
                onChange={(v) => set({ baseline_steps: v })}
                options={[
                  { value: 3000, label: 'Under 4,000' },
                  { value: 5000, label: '4,000 – 6,000' },
                  { value: 7500, label: '6,000 – 9,000' },
                  { value: 10000, label: '9,000+' },
                ]}
              />
            </Field>
          </>
        )}

        {current === 'health' && (
          <>
            <h1 className="text-page-title mb-2">Health context</h1>
            <p className="text-body-sm text-textSoft mb-5">
              The app uses this to choose exercises and to know when to tell you to eat rather than train. It will never interpret any of
              it, change any medication, or give you a diagnosis — all of that stays with your clinicians.
            </p>
            <Field label="Bone health" id="osteo">
              <Choice
                value={form.health.osteoporosis}
                onChange={(v) => setHealth({ osteoporosis: v })}
                options={[
                  { value: true, label: 'Osteoporosis diagnosed' },
                  { value: false, label: 'Not applicable' },
                ]}
              />
            </Field>
            <Field label="Hormonal context" id="horm" hint="In your own words. The app deliberately does not pick between perimenopause, POI and early menopause.">
              <TextArea id="horm" rows={2} value={form.health.hormonal_context} onChange={(e) => setHealth({ hormonal_context: e.target.value })} />
            </Field>
            <Field label="HRT" id="hrt" hint="For your reference only. The app never touches dosing.">
              <TextInput id="hrt" value={form.health.hrt_note} onChange={(e) => setHealth({ hrt_note: e.target.value })} />
            </Field>
            <Field label="Supplements" id="supp">
              <TextInput id="supp" value={form.health.supplements_note} onChange={(e) => setHealth({ supplements_note: e.target.value })} />
            </Field>
            <Field label="Migraines" id="mig">
              <Choice
                value={form.health.migraine_prone}
                onChange={(v) => setHealth({ migraine_prone: v })}
                options={[
                  { value: true, label: 'Yes, regularly' },
                  { value: false, label: 'Not really' },
                ]}
              />
            </Field>
            {form.health.migraine_prone && (
              <Field label="What tends to bring them on" id="trig">
                <TextArea id="trig" rows={2} value={form.health.migraine_triggers} onChange={(e) => setHealth({ migraine_triggers: e.target.value })} />
              </Field>
            )}
          </>
        )}

        {current === 'training' && (
          <>
            <h1 className="text-page-title mb-2">When will you train?</h1>
            <p className="text-body-sm text-textSoft mb-5">
              Three sessions a week. Every session mixes strength and cardio, so there are no cardio-only days to dread.
            </p>
            <Field label="Days" id="days" hint="Tap to change. Three days, and the plan lays itself out around them.">
              <DayPicker value={form.training_days} onChange={(v) => set({ training_days: v })} />
            </Field>
            <Field label="How long have you got?" id="mins">
              <Choice
                columns={3}
                value={form.session_minutes}
                onChange={(v) => set({ session_minutes: v })}
                options={[
                  { value: 30, label: '30 min' },
                  { value: 40, label: '40 min' },
                  { value: 45, label: '45 min' },
                ]}
              />
            </Field>
            <p className="text-meta text-textMute">
              There is a 20-minute version of every session available on the night, for when that is all you have got. It counts.
            </p>
          </>
        )}

        {current === 'targets' && (
          <>
            <h1 className="text-page-title mb-2">Your daily targets</h1>
            {suggested && <p className="text-body-sm text-textSoft mb-5">{suggested.explanation}</p>}

            <Field label="Calorie range" id="kcal" hint="A range, not a number. Landing anywhere inside it is a good day.">
              <div className="flex items-center gap-2">
                <NumberInput id="kcal" value={form.targets.kcal_low} onChange={(e) => setTargets({ kcal_low: e.target.value })} />
                <span className="text-textMute">to</span>
                <NumberInput value={form.targets.kcal_high} onChange={(e) => setTargets({ kcal_high: e.target.value })} />
              </div>
            </Field>

            <Field label="Protein" id="prot" hint="The one number worth tracking properly. It is what keeps muscle on while body fat comes off, and it matters for bone too.">
              <NumberInput id="prot" suffix="g" value={form.targets.protein_g} onChange={(e) => setTargets({ protein_g: e.target.value })} />
            </Field>

            <Field label="Daily steps" id="st" hint="Set a little above where you are now, not double it.">
              <NumberInput id="st" value={form.targets.step_target} onChange={(e) => setTargets({ step_target: e.target.value })} />
            </Field>

            <div className="card-flat text-body-sm text-textSoft">
              <strong className="text-text block mb-1">These are yours to change.</strong>
              The app will never move them on its own. If it thinks something should change, it will ask, tell you why, and take no for
              an answer.
            </div>
          </>
        )}

        {error && <p className="text-body-sm text-crit mt-4">{error}</p>}
      </Card>

      <div className="flex gap-3 mt-5">
        {step > 0 && (
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)} disabled={busy}>
            <ArrowLeft size={18} /> Back
          </Button>
        )}
        <Button onClick={next} disabled={busy} className="flex-1">
          {busy ? 'Setting up…' : step === STEPS.length - 1 ? (<><Check size={18} /> Start the programme</>) : (<>Next <ArrowRight size={18} /></>)}
        </Button>
      </div>
    </div>
  );
}

const DAYS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' }, { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' }, { n: 7, label: 'Sun' },
];

function DayPicker({ value, onChange }) {
  function toggle(n) {
    if (value.includes(n)) {
      if (value.length <= 1) return;
      onChange(value.filter((d) => d !== n));
    } else {
      // Keep it to three, dropping the oldest choice — the programme is built
      // around three sessions and more is not the goal here.
      const next = [...value, n].slice(-3).sort((a, b) => a - b);
      onChange(next);
    }
  }

  return (
    <div className="grid grid-cols-7 gap-1.5">
      {DAYS.map((d) => {
        const on = value.includes(d.n);
        return (
          <button
            key={d.n}
            type="button"
            onClick={() => toggle(d.n)}
            aria-pressed={on}
            className={`py-2.5 rounded-inner text-body-sm font-medium border transition-colors ${
              on ? 'bg-accent text-onAccent border-accent' : 'bg-panel text-textMute border-edge'
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}
